'use client';

import type { Donation } from '@/lib/types';
import { fmtMoney } from '@/lib/format';
import { HIDDEN_AMOUNT, useAmountsHidden } from '@/lib/hideAmounts';

const MONTHS = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/** The year's gifts summed by month, twelve numbers. */
export function byMonth(donations: Donation[], year: number): number[] {
  const out = Array<number>(12).fill(0);
  for (const d of donations) {
    if (!d.date.startsWith(String(year))) continue;
    const m = Number(d.date.slice(5, 7)) - 1;
    if (m >= 0 && m < 12) out[m] += d.amount;
  }
  return out;
}

/** A month's total short enough to sit over a 30-unit bar: $850, $2.2K, $12K. */
function compactMoney(n: number): string {
  if (n < 1000) return `$${Math.round(n)}`;
  if (n < 10000) return `$${(n / 1000).toFixed(1).replace(/\.0$/, '')}K`;
  return `$${Math.round(n / 1000)}K`;
}

/**
 * Twelve bars, one per month, each with its total above it (compact; the exact
 * figure is in the tooltip) and the biggest month emphasized. Plain SVG: the
 * whole point is a glance, not a dashboard. `dark` for the navy card.
 */
export function MonthlyBars({
  donations,
  year,
  dark = false,
  className = '',
}: {
  donations: Donation[];
  year: number;
  dark?: boolean;
  className?: string;
}) {
  const totals = byMonth(donations, year);
  const max = Math.max(...totals, 0);
  const peak = totals.indexOf(max);
  const W = 360;
  const H = 96;
  const top = 16;
  const base = H - 16;
  const slot = W / 12;
  const bar = slot * 0.58;
  const hidden = useAmountsHidden();
  const money = (n: number) => (hidden ? HIDDEN_AMOUNT : fmtMoney(n));
  const ink = dark ? '#ffffff' : '#1c2b39';
  const faint = dark ? 'rgba(255,255,255,0.14)' : '#e5e7eb';
  const fill = dark ? '#4cb8ab' : '#299c8f';
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={`block h-auto w-full ${className}`}
      role="img"
      aria-label={`Giving by month in ${year}`}
    >
      {totals.map((t, i) => {
        const h = max > 0 ? Math.max(t > 0 ? 3 : 0, ((base - top) * t) / max) : 0;
        const x = i * slot + (slot - bar) / 2;
        return (
          <g key={i}>
            <title>{`${MONTH_NAMES[i]} ${year}: ${money(t)}`}</title>
            <rect x={x} y={base - 2} width={bar} height={2} rx={1} fill={faint} />
            {h > 0 && (
              <rect
                x={x}
                y={base - h}
                width={bar}
                height={h}
                rx={3}
                fill={fill}
                opacity={i === peak ? 1 : 0.7}
              />
            )}
            {t > 0 && (
              <text
                x={i * slot + slot / 2}
                y={base - h - 4}
                textAnchor="middle"
                fontSize={8}
                fontWeight={i === peak ? 800 : 600}
                fill={ink}
                opacity={i === peak ? 1 : 0.7}
                className="pii"
              >
                {hidden ? '•••' : compactMoney(t)}
              </text>
            )}
            <text
              x={i * slot + slot / 2}
              y={H - 3}
              textAnchor="middle"
              fontSize={9}
              fontWeight={700}
              fill={ink}
              opacity={0.55}
            >
              {MONTHS[i]}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
